/**
 * THE GUARANTEED INCOME ON A DOWNSIZING PLAN, read once for the card, the hand-built plan and the advice
 * document (owner, 21 Oct 2026 — the guaranteed income review and the agreed mock-up, docs/ux-review/annuities/,
 * screen 6: downsizing works as the village).
 *
 * From a plan's deployment and its own projection rows: who the guaranteed income is for (named as the
 * screens name them), how much goes in, its type in the family panel's terms (full amount back, money back,
 * income only), about what it pays a year, and — for a tested plan — the sibling plan with the same sale and
 * the money invested instead (`dzInvestSiblingIdOf`), which is what the family panel and the document set it
 * against. A hand-built plan has no sibling, so no comparison.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { deploymentAnnuityTerms, type ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { familyPanelKindOf, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { isIncomeOnlyAnnuity } from '../../components/strategy/IncomeOnlyTick.tsx';
import { dzInvestSiblingIdOf } from './downsizerScenario.tsx';

/** The comparison's words, as the panel and the document print them. */
export const DZ_COMPARE_NOTE = 'the same sale, with the money invested instead';

export interface DzGuaranteedIncome {
    /** Who it is for: the owner's first name, or both names for a joint one ("Pat and Sam"). */
    personName: string;
    amount: number;
    kind: FamilyPanelKind;
    /** About what it pays a year, from the plan's first projection year; null when the rows do not say. */
    incomeAYear: number | null;
    /** Lifetime, nothing returned: the type that needs the client's confirmation. */
    incomeOnly: boolean;
    annuityType: 'lifetime' | 'fixedTerm' | 'guaranteed';
    residualPercent: number;
}

type Row = { assets?: { annuities?: Array<{ id?: unknown; annualPayment?: unknown }> } | Record<string, unknown> };

/** The owner's name as the screens and the document say it. */
export function dzAnnuityPersonName(factFind: DraftFactFind | null | undefined, owner: unknown): string {
    const client = (factFind?.client?.firstName || '').trim() || 'the client';
    const partner = (factFind?.partner?.firstName || '').trim() || 'the partner';
    if (owner === 'partner') return partner;
    if (owner === 'joint' && factFind?.adviceFor === 'couple') return `${client} and ${partner}`;
    return client;
}

/** The yearly income of the plan's OWN guaranteed income (not one already on the file), from its first year. */
export function dzAnnuityIncomeOf(rows: readonly unknown[] | null | undefined, factFind: DraftFactFind | null | undefined): number | null {
    const first = (rows || [])[0] as Row | undefined;
    const list = ((first?.assets as { annuities?: Array<{ id?: unknown; annualPayment?: unknown }> } | undefined)?.annuities) || [];
    const onFile = new Set((((factFind?.assets as { annuities?: Array<{ id?: unknown }> } | undefined)?.annuities) || []).map((a) => String(a?.id)));
    const total = list.filter((a) => !onFile.has(String(a?.id))).reduce((t, a) => t + (Number(a?.annualPayment) || 0), 0);
    return total > 0 ? Math.round(total) : null;
}

/** The guaranteed income a plan's deployment buys, or null when it buys none. */
export function dzGuaranteedIncomeOf(
    deployment: ProceedsDeploymentValue | Record<string, unknown> | null | undefined,
    factFind: DraftFactFind | null | undefined,
    rows?: readonly unknown[] | null,
): DzGuaranteedIncome | null {
    const terms = deploymentAnnuityTerms(deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
    if (!terms) return null;
    const amount = Number(terms.purchaseAmount) || 0;
    const annuityType = (terms.annuityType === 'guaranteed' || terms.annuityType === 'fixedTerm' ? terms.annuityType : 'lifetime') as DzGuaranteedIncome['annuityType'];
    const residualPercent = Number(terms.residualPercent) || 0;
    return {
        personName: dzAnnuityPersonName(factFind, terms.owner),
        amount,
        kind: familyPanelKindOf({ annuityType, annuityResidualPercent: residualPercent }),
        incomeAYear: dzAnnuityIncomeOf(rows, factFind),
        incomeOnly: isIncomeOnlyAnnuity({ purchaseAmount: amount, annuityType, residualCapital: residualPercent / 100 }),
        annuityType,
        residualPercent,
    };
}

/** The tested plan with the same sale and the money invested instead, when it was evaluated. */
export function dzInvestSiblingOf<T extends { id?: unknown }>(id: unknown, all: readonly T[] | null | undefined): T | null {
    const sibling = dzInvestSiblingIdOf(id);
    if (!sibling) return null;
    return (all || []).find((s) => String(s.id) === sibling) || null;
}
